import { useState, useEffect, useMemo } from 'react';

import {
  and,
  collection,
  CollectionReference,
  getDocs,
  or,
  Query,
  QueryFieldFilterConstraint,
  QueryFilterConstraint,
} from 'firebase/firestore';
import { query, where } from 'firebase/firestore';

import { useAuth } from '../contextProviders/AuthProvider';
import { useData } from '../contextProviders/DataProvider';
import { ORG_MEMBER_TYPES, OrgMemberType } from '../enums/OrgMemberType';
import { firestore } from '../firebase';
import { MembershipStatus, MEMBERSHIP_STATUSES } from '../types/Membership';
import Person, { PERSON_STATUSES } from '../types/Person';

interface UsePersonsProps {
  isActiveUser?: boolean;
  membershipStatus?: MembershipStatus[];
  orgMemberType?: OrgMemberType;
  orgId?: string; // Rather than find all connected people to the current user, find all people in a specific org
  excludeOrgIds?: string[]; // Don't include the people in these orgs.
}

// Helper function to chunk array into groups of specified size
function chunkArray<T>(array: T[], size: number): T[][] {
  const chunks: T[][] = [];
  for (let i = 0; i < array.length; i += size) {
    chunks.push(array.slice(i, i + size));
  }
  return chunks;
}

// Helper function to deduplicate persons by itemId
function deduplicatePersons(persons: Person[]): Person[] {
  const seenIds = new Set<string>();
  const uniquePersons: Person[] = [];

  for (const person of persons) {
    if (!seenIds.has(person.itemId)) {
      seenIds.add(person.itemId);
      uniquePersons.push(person);
    }
  }

  return uniquePersons;
}

// Helper to check if statuses array includes a given status (handles both new and deprecated values)
function includesStatus(statuses: MembershipStatus[], ...targets: MembershipStatus[]): boolean {
  return targets.some(target => statuses.includes(target));
}

// Determine which Person array fields to query based on orgMemberType and membershipStatus
function getFieldsToQuery(
  orgMemberType: OrgMemberType,
  membershipStatuses: MembershipStatus[]
): string[] {
  const fields: string[] = [];

  // Determine membership types to include
  const includeMember = orgMemberType === ORG_MEMBER_TYPES.MEMBER || orgMemberType === ORG_MEMBER_TYPES.ALL;
  const includeAdmin = orgMemberType === ORG_MEMBER_TYPES.ADMIN || orgMemberType === ORG_MEMBER_TYPES.ALL;

  // Determine statuses to include (handle both new and deprecated values)
  const includeActive = includesStatus(membershipStatuses, MEMBERSHIP_STATUSES.ACTIVE, MEMBERSHIP_STATUSES.DEPRECATED_ACTIVE);
  const includePending = includesStatus(membershipStatuses, MEMBERSHIP_STATUSES.PENDING);
  const includeInactive = includesStatus(membershipStatuses, MEMBERSHIP_STATUSES.INACTIVE);

  // Build field list
  if (includeMember && includeActive) fields.push('orgIds');
  if (includeMember && includePending) fields.push('pendingOrgIds');
  if (includeMember && includeInactive) fields.push('inactiveOrgIds');
  if (includeAdmin && includeActive) fields.push('adminOrgIds');
  if (includeAdmin && includePending) fields.push('pendingAdminOrgIds');
  if (includeAdmin && includeInactive) fields.push('inactiveAdminOrgIds');

  return fields;
}

// Query persons for a single org using OR queries (efficient for single org)
async function querySingleOrg(
  orgId: string,
  fields: string[],
  baseConditions: QueryFilterConstraint[]
): Promise<Person[]> {
  if (fields.length === 0) {
    return [];
  }

  // Build OR query for multiple fields
  const fieldConditions = fields.map(field =>
    where(field, 'array-contains', orgId)
  );

  const q = query(
    collection(firestore, 'profile') as CollectionReference<Person>,
    and(...baseConditions, or(...fieldConditions))
  ) as Query<Person>;

  const snapshot = await getDocs(q);
  const persons = snapshot.docs.map(doc => doc.data());

  // Sort by name on client
  return persons.sort((a, b) => {
    const nameA = a.name?.toLowerCase() || '';
    const nameB = b.name?.toLowerCase() || '';
    return nameA.localeCompare(nameB);
  });
}

// Query persons for multiple orgs using chunked array-contains-any queries
async function queryMultipleOrgs(
  orgIds: string[],
  fields: string[],
  baseConditions: QueryFilterConstraint[]
): Promise<Person[]> {
  if (fields.length === 0 || orgIds.length === 0) {
    return [];
  }

  // Chunk orgIds into groups of 10 (Firestore array-contains-any limit)
  const orgIdChunks = chunkArray(orgIds, 10);

  // Build all queries (fields × chunks)
  const queryPromises: Promise<Person[]>[] = [];

  for (const field of fields) {
    for (const chunk of orgIdChunks) {
      const q = query(
        collection(firestore, 'profile') as CollectionReference<Person>,
        and(...baseConditions, where(field, 'array-contains-any', chunk))
      ) as Query<Person>;

      queryPromises.push(
        getDocs(q).then(snapshot => snapshot.docs.map(doc => doc.data()))
      );
    }
  }

  // Execute all queries in parallel
  const results = await Promise.all(queryPromises);

  // Flatten and deduplicate
  const allPersons = results.flat();
  const uniquePersons = deduplicatePersons(allPersons);

  // Sort by name after deduplication
  return uniquePersons.sort((a, b) => {
    const nameA = a.name?.toLowerCase() || '';
    const nameB = b.name?.toLowerCase() || '';
    return nameA.localeCompare(nameB);
  });
}

export default function usePersons(props: UsePersonsProps) {
  const {
    isActiveUser = true,
    membershipStatus = [MEMBERSHIP_STATUSES.ACTIVE],
    orgMemberType = ORG_MEMBER_TYPES.MEMBER,
    orgId = undefined,
    excludeOrgIds = []
  } = props;

  const { isGod } = useAuth();
  const { isOrgMemberOrAdmin, userState, getAllActiveOrgs, getAdminOrgs } = useData();

  // Memoize these values
  const adminOrgs = useMemo(() => getAdminOrgs(), [getAdminOrgs]);
  const activeOrgs = useMemo(() => getAllActiveOrgs(), [getAllActiveOrgs]);

  const [persons, setPersons] = useState<Person[] | null>(null);
  const [isPersonsLoading, setIsPersonsLoading] = useState(true);
  const [personsError, setPersonsError] = useState<Error | null>(null);

  useEffect(() => {
    if (userState.status === 'logged_out' || !userState.userProfile) {
      setPersons(null);
      setIsPersonsLoading(false);
      setPersonsError(null);
      return;
    }

    let isCancelled = false;

    async function fetchPersons() {
      setIsPersonsLoading(true);
      setPersonsError(null);

      try {
        // Build base conditions
        const activeConditions: QueryFieldFilterConstraint[] = [];

        if (isGod && isActiveUser === false) {
          activeConditions.push(where('status', 'not-in', [PERSON_STATUSES.ACTIVE, PERSON_STATUSES.DEPRECATED_ACTIVE]));
        } else {
          activeConditions.push(where('status', 'in', [PERSON_STATUSES.ACTIVE, PERSON_STATUSES.DEPRECATED_ACTIVE]));
        }

        const baseConditions: QueryFilterConstraint[] = [...activeConditions];

        // Get orgIds to query
        const orgIds: string[] = [];
        if (orgId) {
          (isGod || isOrgMemberOrAdmin(orgId)) && orgIds.push(orgId);
        } else {
          const filteredActiveOrgs = activeOrgs?.filter((id) => !excludeOrgIds.includes(id));
          filteredActiveOrgs?.length > 0 && orgIds.push(...filteredActiveOrgs);
        }

        // If no orgIds to query, return empty
        if (orgIds.length === 0) {
          if (!isCancelled) {
            setPersons([]);
            setIsPersonsLoading(false);
          }
          return;
        }

        // Check permissions for non-God users querying inactive/deleted statuses
        const hasPermission =
          isGod ||
          orgIds.every((org) => adminOrgs.includes(org)) ||
          !includesStatus(membershipStatus,
            MEMBERSHIP_STATUSES.INACTIVE,
            MEMBERSHIP_STATUSES.DELETED);

        if (!hasPermission) {
          if (!isCancelled) {
            setPersons([]);
            setIsPersonsLoading(false);
          }
          return;
        }

        // Get fields to query based on orgMemberType and membershipStatus
        const fields = getFieldsToQuery(orgMemberType, membershipStatus);

        // Execute appropriate query strategy
        let result: Person[];
        if (orgIds.length === 1) {
          // Single org: use OR queries for efficiency
          result = await querySingleOrg(orgIds[0], fields, baseConditions);
        } else {
          // Multiple orgs: use chunked array-contains-any queries
          result = await queryMultipleOrgs(orgIds, fields, baseConditions);
        }

        if (!isCancelled) {
          setPersons(result);
          setIsPersonsLoading(false);
        }
      } catch (err) {
        if (!isCancelled) {
          console.error('Error fetching persons:', err);
          setPersonsError(err as Error);
          setIsPersonsLoading(false);
        }
      }
    }

    fetchPersons();

    return () => {
      isCancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isActiveUser, JSON.stringify(membershipStatus), orgMemberType, orgId, JSON.stringify(excludeOrgIds), isGod, adminOrgs, activeOrgs, userState.status]);

  if (userState.status === 'logged_out') {
    return {
      persons: null,
      isPersonsLoading: false,
      personsError: new Error('User must be logged in to view persons'),
    };
  }

  return {
    persons: persons || null,
    isPersonsLoading:
      isPersonsLoading ||
      userState.status === 'authenticating_user' ||
      userState.status === 'authenticating_profile' ||
      userState.status === 'authenticating_memberships',
    personsError,
  };
}
